import {
  Box,
} from '@mui/material';
import Places from '../Places/Places';
import ImageCarousel from '../../Components/ ImageCarousel';
import LocationComponent from '../../Components/Map/LocationComponent';
import { RootState } from '../../store/rootReducers';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import DashboardPlaceScreen from './DashboardPlaceScreen';
import { setDashboard } from '../../store/slices/DashboardSlice';
import DashboardTripScreen from './DashboardTripScreen';
import DashboardTopBar from './DashboardTopBar';
import UserSearchFab from '../../Components/Fab/UserSearchFab';


const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const banners = useSelector((state: RootState) => state.dashboard.introSliders);
  const location = useSelector((state: RootState) => state.location.location);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  
  // Memoize location coordinates to prevent unnecessary API calls
  const locationLat = useMemo(() => location?.lat, [location?.lat]);
  const locationLng = useMemo(() => location?.lng, [location?.lng]);
  
  // Track the last API call coordinates to prevent duplicate calls
  const lastApiCallRef = useRef<{ lat: number; lng: number } | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    // Check if Notification API is available
    if (typeof Notification === 'undefined') {
      return;
    }
    
    if (Notification.permission !== 'granted' && !(window as any).__notificationMsgShown) {
      (window as any).__notificationMsgShown = true;
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: 'Enable notifications from browser settings to never miss updates',
            type: 'warning',
            onClick: async () => {
              if (typeof Notification === 'undefined') return;
              const result = await Notification.requestPermission();
              if (result === 'granted') {
                window.dispatchEvent(
                  new CustomEvent('app-toast', {
                    detail: { message: 'Notifications enabled!', type: 'success' },
                  }),
                );
              } else {
                window.open('https://support.google.com/chrome/answer/3220216', '_blank');
              }
            },
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    const fetchLanding = async () => {
      // Skip if already fetching
      if (isFetchingRef.current) return;
      
      let endpoint = '/landing';
      let shouldFetch = false;
      
      // Add location parameters if available
      if (locationLat && locationLng) {
        // Check if coordinates have meaningfully changed (avoid calls for tiny coordinate changes)
        const lastCall = lastApiCallRef.current;
        const hasChanged = !lastCall || 
          Math.abs(lastCall.lat - locationLat) > 0.0001 || 
          Math.abs(lastCall.lng - locationLng) > 0.0001;
        
        if (hasChanged) {
          const queryParams = new URLSearchParams({
            latitude: locationLat.toString(),
            longitude: locationLng.toString(),
          });
          endpoint = `${endpoint}?${queryParams.toString()}`;
          shouldFetch = true;
          lastApiCallRef.current = { lat: locationLat, lng: locationLng };
        }
      } else {
        // Fetch without location if location is not available
        shouldFetch = !lastApiCallRef.current;
        if (shouldFetch) {
          lastApiCallRef.current = { lat: 0, lng: 0 }; // Mark as called
        }
      }
      
      if (shouldFetch) {
        isFetchingRef.current = true;
        try {
          const res = await request(endpoint, {
            method: 'GET',
            authorization: isLoggedIn,
          }) as any;
          dispatch(setDashboard({
            banners: res.data.banners,
            introSliders: res.data.introSliders,
            categories: res.data.postCategories,
            newPlaces: res.data.newPlaces,
            curatedPlaces: res.data.curatedPlaces,
            tripsItinerary: res.data.tripsItinerary,
          }));
        } catch (error) {
          console.error('Error fetching landing data:', error);
        } finally {
          isFetchingRef.current = false;
        }
      }
    };
    
    fetchLanding();
  }, [locationLat, locationLng, dispatch, isLoggedIn]);

  // Check intro slider condition and navigate if needed
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenIntroSlider');
    if (!hasSeenIntro && banners && banners.length > 0) {
      navigate('/intro');
    }
  }, [banners, navigate]);

  return (
    <>
      <DashboardTopBar />
          <ImageCarousel/>
      <Box p={2} sx={{ mb:6 }}>
          <DashboardPlaceScreen title="Nearby places" />
          <DashboardTripScreen  />
          <DashboardPlaceScreen title="Newly added places" noCategories />
        {/* <Places/> */}
      </Box>
      <LocationComponent/>
      <UserSearchFab />
    </>
  );
};

export default Dashboard;
